import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { binaryTreeAPI } from '../api/binary-tree';
import { useAuthStore } from '../store/auth';
import { TopBar } from '../components/ui/TopBar';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import type { PlacementStepNode } from '../components/PlacementTreePicker';

type Breadcrumb = { id: number; username: string };

export function BinaryTree() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const profile = useAuthStore((s) => s.profile) as Record<string, unknown> | null;
  const rootUserId = profile?.id as number | undefined;
  const rootUsername = (profile?.username as string | undefined) ?? '—';

  const [currentUserId, setCurrentUserId] = useState<number | undefined>(undefined);
  const [breadcrumb, setBreadcrumb] = useState<Breadcrumb[]>([]);

  useEffect(() => {
    if (rootUserId && rootUsername && breadcrumb.length === 0) {
      setCurrentUserId(rootUserId);
      setBreadcrumb([{ id: rootUserId, username: rootUsername }]);
    }
  }, [rootUserId, rootUsername, breadcrumb.length]);

  const stepQuery = useQuery({
    queryKey: ['binary-tree', 'placement-step', currentUserId],
    queryFn: () => binaryTreeAPI.getPlacementStep({ user_id: currentUserId }) as Promise<PlacementStepNode>,
    enabled: !!currentUserId,
    staleTime: 15_000,
  });

  const step = stepQuery.data;

  function navigateTo(userId: number, crumb: Breadcrumb[]) {
    setCurrentUserId(userId);
    setBreadcrumb(crumb);
  }

  function drillTo(child: { id: number; username: string }) {
    navigateTo(child.id, [...breadcrumb, { id: child.id, username: child.username }]);
  }

  function goToCrumb(index: number) {
    const next = breadcrumb.slice(0, index + 1);
    navigateTo(next[next.length - 1]!.id, next);
  }

  function goRegister(userId: number, position: 'L' | 'R') {
    navigate(`/register-member?placement_user_id=${userId}&position=${position}`);
  }

  const isRoot = currentUserId === rootUserId;

  return (
    <div className="min-h-screen bg-surface-base pb-20">
      <div className="absolute inset-0 -z-10 bg-space opacity-70" />
      <TopBar title={t('common.binaryTree', '安置树')} />

      <div className="px-4 pt-4">
        <Card className="p-4">
          <p className="text-xs text-text-muted">
            {t('registerMember.treeHintDrill', '先点「旗下成员」一层层往下；到达目标节点后，点「左区/右区注册」。')}
          </p>
        </Card>
      </div>

      <div className="px-4 pt-4 pb-8 space-y-3">
        {breadcrumb.length > 0 && (
          <div className="flex flex-wrap items-center gap-1 text-xs px-1">
            {breadcrumb.map((crumb, index) => (
              <span key={crumb.id} className="flex items-center gap-1">
                {index > 0 && <span className="text-text-muted">›</span>}
                <button
                  type="button"
                  onClick={() => goToCrumb(index)}
                  className={`font-semibold ${index === breadcrumb.length - 1 ? 'text-brand-300' : 'text-text-primary'}`}
                >
                  {crumb.username}
                </button>
              </span>
            ))}
          </div>
        )}

        {stepQuery.isLoading && !step && (
          <div className="space-y-3">
            <LoadingSkeleton className="h-16" />
            <LoadingSkeleton className="h-16" />
          </div>
        )}

        {stepQuery.isError && !step && (
          <EmptyState title={t('common.notInBinaryTree', '您还未加入二元树')} />
        )}

        {step && (
          <Card
            className={`p-4 ${isRoot ? 'border-brand-500/35 bg-brand-500/10 shadow-glow-brand' : ''}`}
          >
            <div className="mb-3">
              <div className="text-[10px] text-text-muted">{t('registerMember.currentNode', '当前节点')}</div>
              <div className={`text-sm font-semibold ${isRoot ? 'text-brand-300' : 'text-text-primary'}`}>
                {step.username}
              </div>
              <div className="text-[10px] text-text-muted font-mono">
                ID:{step.id} · {step.level_code}
                {step.position ? ` · ${step.position}` : ''}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => goRegister(step.id, 'L')}
                className="rounded-lg border border-brand-500/40 bg-brand-500/10 px-2 py-2 text-[10px] font-semibold text-brand-300"
              >
                {step.has_left
                  ? t('registerMember.placeLeftSpill', '左区注册（滑落）')
                  : t('registerMember.placeLeft', '+ 左区注册')}
              </button>
              <button
                type="button"
                disabled={!step.has_left}
                onClick={() => {
                  if (!step.has_left) return;
                  goRegister(step.id, 'R');
                }}
                className="rounded-lg border border-brand-500/40 bg-brand-500/10 px-2 py-2 text-[10px] font-semibold text-brand-300 disabled:opacity-40"
              >
                {!step.has_left
                  ? t('registerMember.placeRightNeedLeft', '请先落左区 2.1')
                  : step.has_right
                    ? t('registerMember.placeRightSpill', '右区注册（滑落）')
                    : t('registerMember.placeRight', '+ 右区注册')}
              </button>
            </div>

            {(step.left || step.right) && (
              <>
                <div className="my-3 h-px bg-brand-500/15" />
                <div className="mb-2 text-[10px] font-semibold text-text-muted">
                  {t('registerMember.drillDown', '旗下成员 — 点击进入下一层')}
                </div>
                <div className="flex flex-col gap-2">
                  {step.left ? (
                    <button
                      type="button"
                      onClick={() => drillTo(step.left!)}
                      className="flex w-full items-center justify-between rounded-lg border border-surface-border bg-surface-card/55 px-3 py-2.5 text-left text-xs font-semibold text-text-primary"
                    >
                      <span><span className="text-token-ep1">L</span> · {step.left.username}</span>
                      <span className="text-text-muted">›</span>
                    </button>
                  ) : null}
                  {step.right ? (
                    <button
                      type="button"
                      onClick={() => drillTo(step.right!)}
                      className="flex w-full items-center justify-between rounded-lg border border-surface-border bg-surface-card/55 px-3 py-2.5 text-left text-xs font-semibold text-text-primary"
                    >
                      <span><span className="text-token-ep2">R</span> · {step.right.username}</span>
                      <span className="text-text-muted">›</span>
                    </button>
                  ) : null}
                </div>
              </>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
